import React, { useMemo } from "react";

import Alert from "veneer/Alert";
import { ChartData } from "veneer/Chart";

import ResourceLineChart from "./ResourceLineChart";
import { ChartPoint } from "../model";

interface DiskChartProps {
  data: ReadonlyArray<ChartPoint>;
}

interface DiskUsagePoint {
  x: number;
  y: number;
}

function getChartData(data: ReadonlyArray<ChartPoint>): ChartData<"line"> {
  const dataByLabel = data.reduce<Record<string, DiskUsagePoint[]>>(
    (acc, point) => {
      const [timestamp, free, total, label = "Disk"] = point;

      if (!acc[label]) {
        acc[label] = [];
      }

      acc[label].push({
        x: timestamp,
        y: Number((((total - free) / total) * 100).toFixed(2)),
      });

      return acc;
    },
    {},
  );

  return {
    datasets: Object.entries(dataByLabel).map(([label, points]) => ({
      borderWidth: 1,
      data: points,
      label,
      pointRadius: 0,
    })),
  };
}

function formatTooltipValue(label: string, value: number): string {
  return `${label}: ${value.toFixed(2)}%`;
}

export default function DiskChart({ data }: DiskChartProps) {
  const chartData = useMemo(() => getChartData(data), [data]);
  const timestamps = useMemo(
    () => data.map(([timestamp]) => timestamp),
    [data],
  );

  if (data.length === 0) {
    return (
      <Alert dense severity="info">
        Disk usage data is not available for your device.
      </Alert>
    );
  }

  return (
    <ResourceLineChart
      data={chartData}
      formatTooltipValue={formatTooltipValue}
      timestamps={timestamps}
    />
  );
}
